import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  CalendarClock,
  CreditCard,
  Edit3,
  Factory,
  FileText,
  Mail,
  MapPin,
  Phone,
  Plus,
  ScrollText,
  Search,
  ShieldCheck,
  User,
  Wallet
} from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { createSupplier, listSuppliers, toFinanceSupplier, toSupplierPayload, updateSupplier } from '../../../../../services/SuppliersService';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import { usePaymentConditions } from '../../../../../hooks/usePaymentConditions';
import {
  FinanceSupplier,
  PRESET_SUPPLIERS,
  formatEuro,
  fullAddress,
  nextCode
} from './financeMasters';
import { AddressFormSection, AttachmentsCard, AttachmentsFormSection } from './MasterParts';
import { FinanceAccountItems } from './FinanceAccountItems';
import '../../shared-modules.css';
import './FinanceMasters.css';

type SupplierForm = Omit<FinanceSupplier, 'id' | 'pendingBalance'>;

const EMPTY_FORM: SupplierForm = {
  name: '',
  cif: '',
  type: 'Proveedor',
  contactPerson: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  postalCode: '',
  province: '',
  country: 'España',
  paymentTerms: 'Neto 30 días',
  preferredPaymentMethod: 'Transferencia bancaria',
  account: '400000',
  creditLimit: 10000,
  notes: '',
  attachments: [],
  status: 'Activo'
};

const ACCOUNT_BY_TYPE: Record<FinanceSupplier['type'], string> = {
  Proveedor: '400000',
  Acreedor: '410000'
};

interface FinanceSuppliersViewProps {
  privacyMode: boolean;
}

export const FinanceSuppliersView = ({ privacyMode }: FinanceSuppliersViewProps) => {
  const { notify } = useToast();

  // Proveedores reales del backend (suppliers_list)
  const [suppliers, setSuppliers] = useState<FinanceSupplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { conditions } = usePaymentConditions();
  const { methods } = usePaymentMethods();

  useEffect(() => {
    let cancelled = false;
    listSuppliers({ pageSize: 500 })
      .then(list => { if (!cancelled) setSuppliers(list.map(toFinanceSupplier)); })
      .catch(err => {
        console.error('Error fetching suppliers:', err);
        if (!cancelled) notify('Error', 'No se pudieron cargar los proveedores.', 'warning');
      })
      .then(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const [selectedSupplier, setSelectedSupplier] = useState<FinanceSupplier | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<SupplierForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  // Partidas del acreedor, a pantalla completa
  const [itemsSupplier, setItemsSupplier] = useState<FinanceSupplier | null>(null);

  const setField = <K extends keyof SupplierForm>(field: K, value: SupplierForm[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleCreateNewClick = () => {
    setSelectedSupplier(null);
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (supplier: FinanceSupplier) => {
    const { id, pendingBalance, ...rest } = supplier;
    setSelectedSupplier(supplier);
    setFormData({ ...EMPTY_FORM, ...rest, attachments: [...(supplier.attachments || [])] });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectSupplier = (supplier: FinanceSupplier) => {
    setIsFormOpen(false);
    setSelectedSupplier(supplier);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedSupplier(null);
  };

  const openItems = (supplier: FinanceSupplier) => {
    closePanel();
    setItemsSupplier(supplier);
  };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedSupplier) && !isFormOpen);

  const handleTypeChange = (type: FinanceSupplier['type']) =>
    setFormData(prev => ({ ...prev, type, account: ACCOUNT_BY_TYPE[type] }));

  const handleSaveSupplier = async () => {
    if (!formData.name.trim() || !formData.cif.trim()) {
      setFormError('La razón social y el CIF son obligatorios.');
      return;
    }
    if (formData.creditLimit < 0) {
      setFormError('El límite de crédito no puede ser negativo.');
      return;
    }
    const data: SupplierForm = {
      ...formData,
      name: formData.name.trim(),
      cif: formData.cif.toUpperCase().trim()
    };
    const payload = toSupplierPayload(
      { ...data, address: fullAddress(data) },
      conditions.find(c => c.name === data.paymentTerms)?.days ?? 30
    );

    setSaving(true);
    setFormError('');
    try {
      if (isEditing && selectedSupplier) {
        const record = await updateSupplier(selectedSupplier.id, payload);
        // Lo que el backend no guarda se conserva en pantalla hasta recargar
        const updated: FinanceSupplier = { ...selectedSupplier, ...data, id: record.id };
        setSuppliers(prev => prev.map(s => (s.id === updated.id ? updated : s)));
        setSelectedSupplier(updated);
        notify('Éxito', `${updated.type} "${updated.name}" modificado correctamente.`, 'info');
      } else {
        const record = await createSupplier(payload);
        const created: FinanceSupplier = { ...data, id: record.id, pendingBalance: 0 };
        setSuppliers(prev => [created, ...prev]);
        setSelectedSupplier(created);
        notify('Éxito', `${created.type} "${created.name}" dado de alta.`, 'info');
      }
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving supplier:', err);
      setFormError(err instanceof Error ? err.message : 'No se pudo guardar el proveedor.');
    } finally {
      setSaving(false);
    }
  };

  const query = searchQuery.toLowerCase();
  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(query) ||
    s.cif.toLowerCase().includes(query) ||
    s.contactPerson.toLowerCase().includes(query)
  );

  const columns: ColumnDef<FinanceSupplier>[] = [
    {
      id: 'id',
      header: 'CÓDIGO',
      render: (s) => <span className="fin-master__code">{s.id}</span>
    },
    {
      id: 'name',
      header: 'RAZÓN SOCIAL / CIF',
      render: (s) => (
        <>
          <div className="fin-master__name">{s.name}</div>
          <div className="fin-master__sub">CIF: {s.cif}</div>
        </>
      )
    },
    {
      id: 'contact',
      header: 'CONTACTO',
      render: (s) => (
        <>
          <div className="fin-master__text">{s.contactPerson || '—'}</div>
          <div className="fin-master__sub fin-master__sub--lower">{s.email || '—'}</div>
        </>
      )
    },
    {
      id: 'type',
      header: 'TIPO / CUENTA',
      render: (s) => (
        <>
          <div className="fin-master__text">{s.type}</div>
          <span className="fin-master__chip">{s.account}</span>
        </>
      )
    },
    {
      id: 'paymentTerms',
      header: 'CONDICIONES DE PAGO',
      render: (s) => (
        <>
          <div className="fin-master__text">{s.paymentTerms}</div>
          <div className="fin-master__sub">{s.preferredPaymentMethod}</div>
        </>
      )
    },
    {
      id: 'pendingBalance',
      header: 'SALDO PENDIENTE',
      align: 'right',
      render: (s) => (
        <span className={`fin-master__amount ${s.pendingBalance > 0 ? 'pending' : ''}`}>
          {formatEuro(s.pendingBalance, privacyMode)}
        </span>
      )
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (s) => (
        <span className={`module-badge ${s.status === 'Activo' ? 'active' : 'inactive'}`}>{s.status}</span>
      )
    }
  ];

  // Mientras están abiertas sustituyen al maestro
  if (itemsSupplier) {
    return <FinanceAccountItems kind="acreedor" account={itemsSupplier} privacyMode={privacyMode} onClose={() => setItemsSupplier(null)} />;
  }

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR RAZÓN SOCIAL, CIF O PERSONA DE CONTACTO..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nuevo_Proveedor
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedSupplier || isFormOpen)}>
        <DataTable
          data={filteredSuppliers}
          columns={columns}
          title={<><Factory size={14} className="module-panel-title-icon" /> Maestro_De_Proveedores_Y_Acreedores</>}
          subtitle={`TOTAL: ${filteredSuppliers.length}`}
          getRowId={(s) => s.id}
          onRowClick={handleSelectSupplier}
          selectedRowId={selectedSupplier?.id}
          emptyText={loading ? 'Cargando proveedores...' : 'No se encontraron proveedores ni acreedores.'}
          actionsHeader="ACCIONES"
          actionsColumn={(s) => (
            <RowActionsMenu
              actions={[
                viewAction(() => handleSelectSupplier(s)),
                editAction(() => handleEditClick(s)),
                { label: 'Ver partidas', icon: ScrollText, tone: 'accent', onSelect: () => openItems(s) }
              ]}
            />
          )}
        />

        {isFormOpen ? (
          <SidebarForm
            expandable
            title={isEditing ? 'Modificar_Ficha_Proveedor' : 'Alta_De_Nuevo_Proveedor'}
            onClose={closePanel}
            submitLabel={saving ? 'Guardando...' : 'Guardar_Proveedor'}
            submitDisabled={saving}
            onSubmit={handleSaveSupplier}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_Identificativos" icon={FileText}>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Razón Social *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Ej. Papelera de Alcalá S.A."
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">CIF / NIF *</label>
                  <input
                    type="text"
                    required
                    value={formData.cif}
                    onChange={(e) => setField('cif', e.target.value.toUpperCase())}
                    placeholder="Ej. A28114422"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Tipo</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleTypeChange(e.target.value as FinanceSupplier['type'])}
                    className="sidebar-form-input"
                  >
                    <option value="Proveedor">Proveedor (Mercancías)</option>
                    <option value="Acreedor">Acreedor (Servicios)</option>
                  </select>
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Persona de Contacto</label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setField('contactPerson', e.target.value)}
                    placeholder="Ej. Fernando Gómez"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setField('status', e.target.value as FinanceSupplier['status'])}
                    className="sidebar-form-input"
                  >
                    <option value="Activo">ACTIVO</option>
                    <option value="Inactivo">INACTIVO</option>
                  </select>
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Email Comercial / Facturación</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="comercial@proveedor.es"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Teléfono</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    placeholder="+34 91 000 0000"
                    className="sidebar-form-input"
                  />
                </div>
              </div>
            </DetailSection>

            <AddressFormSection
              title="2. Dirección_Fiscal"
              address={formData}
              onChange={(field, value) => setField(field, value)}
              streetLabel="Calle, Número o Polígono"
              streetPlaceholder="Polígono Industrial Las Eras 12"
              cityPlaceholder="Alcalá de Henares"
              postalCodePlaceholder="28805"
            />

            <DetailSection title="3. Contabilidad_Y_Pago" icon={BookOpen}>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Cuenta Contable PGC *</label>
                  <select
                    value={formData.account}
                    onChange={(e) => setField('account', e.target.value)}
                    className="sidebar-form-input"
                  >
                    <option value="400000">400000 - Proveedores (Euros)</option>
                    <option value="410000">410000 - Acreedores prestaciones servicios</option>
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Condiciones de Pago *</label>
                  <select
                    value={formData.paymentTerms}
                    onChange={(e) => setField('paymentTerms', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {conditions.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Forma de Pago Preferida</label>
                  <select
                    value={formData.preferredPaymentMethod}
                    onChange={(e) => setField('preferredPaymentMethod', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {methods.map(m => (
                      <option key={m.id} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Límite de Crédito (€)</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={formData.creditLimit}
                    onChange={(e) => setField('creditLimit', parseFloat(e.target.value) || 0)}
                    className="sidebar-form-input"
                  />
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Notas y Condiciones Particulares</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  placeholder="Detalles sobre plazos de entrega, tiradas mínimas, acuerdos de rappel..."
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>

            <AttachmentsFormSection
              title="4. Documentos_Adjuntos"
              attachments={formData.attachments}
              onChange={(attachments) => setField('attachments', attachments)}
            />
          </SidebarForm>
        ) : selectedSupplier ? (
          <DetailPanel
            expandable
            title="Ficha_De_Proveedor"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedSupplier)} />
            }
            cards={<AttachmentsCard attachments={selectedSupplier.attachments} />}
          >
            <DetailIdentity
              icon={Factory}
              reference={`${selectedSupplier.id} • ${selectedSupplier.type}`}
              name={selectedSupplier.name}
              subtitle={
                <span className={`module-badge ${selectedSupplier.status === 'Activo' ? 'active' : 'inactive'}`}>
                  {selectedSupplier.status}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={FileText} label="CIF_FACTURACIÓN">{selectedSupplier.cif}</DetailField>
              <DetailField icon={MapPin} label="DIRECCIÓN_FISCAL">{fullAddress(selectedSupplier) || 'No declarada'}</DetailField>
              <DetailField icon={User} label="PERSONA_DE_CONTACTO">{selectedSupplier.contactPerson || 'No declarada'}</DetailField>
              <DetailField icon={Mail} label="EMAIL_COMERCIAL">
                <span className="detail-field-value lowercase">{selectedSupplier.email || 'No declarado'}</span>
              </DetailField>
              <DetailField icon={Phone} label="TELÉFONO">{selectedSupplier.phone || 'No declarado'}</DetailField>
              <DetailField icon={BookOpen} label="CUENTA_PGC">{selectedSupplier.account}</DetailField>
              <DetailField icon={CalendarClock} label="CONDICIONES_DE_PAGO">{selectedSupplier.paymentTerms}</DetailField>
              <DetailField icon={CreditCard} label="FORMA_DE_PAGO">{selectedSupplier.preferredPaymentMethod || 'No declarada'}</DetailField>
              <DetailField icon={ShieldCheck} label="LÍMITE_DE_CRÉDITO">{formatEuro(selectedSupplier.creditLimit, privacyMode)}</DetailField>
              <DetailField icon={Wallet} label="SALDO_PENDIENTE">{formatEuro(selectedSupplier.pendingBalance, privacyMode)}</DetailField>
            </DetailFieldList>

            {selectedSupplier.notes && <DetailNotes>{selectedSupplier.notes}</DetailNotes>}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceSuppliersView;
